import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { Box, Button, Popover, Typography } from '@mui/material';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { useCurrentDateRange } from 'hooks/dateRange';
import HeaderMenu from './HeaderMenu';
import Loader from './Loader';
import AiAnalysisModal from './components/AiAnalysisModal';
import ProductivityLastHoursModal from './components/ProductivityLastHoursModal';
import AllFleet from './AllFleet';
import Hauler from './Hauler';
import ProductionHandler from 'handlers/production.handler';
import { setIsPrinting } from 'store/exportData.slice';
import { setIsLoading } from 'store/production.slice';
import { type ChartSource } from './AllFleet/utils';
import { useCurrentDailyAvgProd, useRollingDailyAverage } from './utils';
import { exportAnalyticsReport } from 'utils/exports/analyticsReportExports';
import { useShiftHours } from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import useShiftConfig from 'hooks/useShiftConfig';
import { useClientTheme } from 'utils/clientTheme';
import useDebounce from 'hooks/useDebounce';

const Production = () => {
  const { t } = useTranslation();
  const palette = useClientTheme();
  const location = useLocation();
  const typeReceived = location.state?.type;
  const productionHandler = new ProductionHandler();
  const { accessToken } = useAppSelector((state) => state.authReducer);

  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { data, loadingStates } = useAppSelector(
    (state) => state.productionReducer
  );
  const {
    allFleet: { loaderEvents },
  } = data;

  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const { assetOverView: assetOverview, assets } = useAppSelector(
    (state) => state.assetReducer
  );

  const customerCode = useAppSelector(
    (state) => state.persistedReducer.customer.code
  );
  const { isMultiShift } = useShiftConfig();
  const { isPrinting } = useAppSelector((state) => state.exportDataReducer);
  const dispatch = useAppDispatch();

  const enableDarkTheme = isDarkTheme(theme);
  const defaultValue = typeReceived
    ? typeReceived === 'Haul Truck'
      ? 1
      : typeReceived === 'Loader'
      ? 2
      : 0
    : 0;
  const [value, setValue] = useState(defaultValue);
  const [isSelected, setIsSelected] = useState('Today');
  const [, setInitRun] = useState<boolean>(true);
  const [isCustomDateChanged, setIsCustomDateChanged] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isLastHoursModalOpen, setIsLastHoursModalOpen] = useState(false);
  const [lastHoursModalDate, setLastHoursModalDate] = useState<string | null>(
    null
  );
  const [lastHoursAnchor, setLastHoursAnchor] = useState<HTMLElement | null>(
    null
  );
  const [lastHoursPickedDate, setLastHoursPickedDate] = useState<Dayjs>(
    dayjs().subtract(1, 'day')
  );
  const [lastHoursPickedCount, setLastHoursPickedCount] = useState(2);

  const [aggregation, setAggregation] = useState<string>('Daily');

  const [intialDateRange] = useState({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });
  const { startDate, endDate, setStartDate, setEndDate, today } =
    useCurrentDateRange({ initialDate: new Date(intialDateRange.startDate) });
  const [exportTo, setExportTo] = useState('');
  const [, setExportClicked] = useState(false);
  const [detailsVal, setDetailsVal] = useState<Record<string, unknown>>({});
  const [analyticsData, setAnalyticsData] = useState<any[]>();
  const [hoursBreakDown, setHoursBreakDown] = useState<{
    hauler: {
      Loading?: string;
      Unloading?: string;
      'Loaded Trip'?: string;
      'Unloaded Trip'?: string;
    } | null;
    'Fuel Consumption': {
      Loading?: string;
      Unloading?: string;
      'Loaded Trip'?: string;
      'Unloaded Trip'?: string;
    } | null;
    loader: {
      Loading?: string;
      Unloading?: string;
      'Loaded Trip'?: string;
      'Unloaded Trip'?: string;
    } | null;
  }>({
    hauler: null,
    'Fuel Consumption': null,
    loader: null,
  });
  const [shiftWiseAnalysis, setShiftWiseAnalysis] =
    useState<boolean>(isMultiShift);
  const [shiftNumber, setShiftNumber] = useState<number>(1);
  const shiftHours = useShiftHours(shiftWiseAnalysis, shiftNumber);

  const debouncedStartDate = useDebounce(startDate, 500);
  const debouncedEndDate = useDebounce(endDate, 500);
  const debouncedShiftNumber = useDebounce(shiftNumber, 500);
  const debouncedAggregation = useDebounce(aggregation, 500);

  const handleDateChange = (range: [Date | null, Date | null]) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (
      aggregation === 'Monthly' &&
      isSelected === 'Custom' &&
      startDate &&
      endDate
    ) {
      const newStartDate = dayjs(startDate).startOf('month').toDate();
      const newEndDate = dayjs(endDate).endOf('month').toDate();

      const startNeedsUpdate =
        dayjs(startDate).format('YYYY-MM-DD') !==
        dayjs(newStartDate).format('YYYY-MM-DD');
      const endNeedsUpdate =
        dayjs(endDate).format('YYYY-MM-DD') !==
        dayjs(newEndDate).format('YYYY-MM-DD');

      if (startNeedsUpdate || endNeedsUpdate) {
        if (startNeedsUpdate) setStartDate(newStartDate);
        if (endNeedsUpdate) setEndDate(newEndDate);
      }
    }
  }, [aggregation, isSelected, startDate, endDate, setStartDate, setEndDate]);

  const handleExportClick = async (returnType: 'excel' | 'pdf') => {
    dispatch(setIsPrinting(true));
    try {
      await exportAnalyticsReport({
        startDate,
        endDate,
        range: isSelected,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        assetTypeValue: value,
        utilizationType: 'production',
        dispatch,
        fileNamePrefix: 'Production',
        returnType,
        suggestionData: returnType === 'excel' ? suggestionData : undefined,
        getReportBytes: async (
          formattedStartDate: string,
          formattedEndDate: string,
          range: string,
          aggregation: string,
          shiftWiseAnalysis: boolean,
          shiftNumber: number,
          assetType: string,
          utilizationType: string,
          returnType: 'excel' | 'pdf',
          dailyProductiveHoursRange?: string,
          suggestionData?: Record<string, unknown>
        ) =>
          await productionHandler.getProductionReport({
            startDateVal: formattedStartDate,
            endDateVal: formattedEndDate,
            range,
            aggregation,
            shiftWiseAnalysis,
            shiftNumber,
            assetType,
            utilizationType,
            returnType,
            shiftHours,
            suggestionData,
          }),
        errorMessage: t('error_calling_utilization_report_api'),
      });
    } catch (error) {
      console.error('Error exporting production overview:', error);
    } finally {
      dispatch(setIsPrinting(false));
    }
  };
  const [userSelections, setUserSelections] = useState({});
  const [suggestionData, setSuggestionData] = useState({});

  const [selectedChartSource, setSelectedChartSource] =
    useState<ChartSource>('Last 7 Days'); // This could be moved to redux to avoid prop drilling

  useEffect(() => {
    if (exportTo === 'pdf') {
      void handleExportClick('pdf');
    }
    if (exportTo === 'excel') {
      // if (value === 2) {
      //   exportToExcelForProductionLoaders(
      //     data.loaders,
      //     fileNameWithDateTime,
      //     `${title}`,
      //     unitSystem,
      //     analyticsData as any[],
      //     {
      //       ...userSelections,
      //       quickrange:
      //         isSelected === 'Custom'
      //           ? `${calculateDaysDifference(
      //               startDate,
      //               endDate
      //             )} Days from ${convertStdDateString(
      //               startDate
      //             )} with ${aggregation} aggregation`
      //           : isSelected,
      //       aggregation,
      //     },
      //     hoursBreakDown,
      //     assets,
      //     {
      //       startDate,
      //       endDate,
      //     },
      //     loaderEvents
      //   );
      // } else {
      //   exportToExcelForProduction(
      //     {
      //       ...data.allFleet,
      //       fleetSummary: {
      //         'YTD Target (Tons)': detailsVal['YTD Target (Tons)'],
      //         'YTD Actual (Tons)': formatNumberToShortScale(
      //           convertToTwoDecimalPlaces(
      //             data.allFleet.totalFleetDetails.tons?.toString()
      //           )
      //         ),
      //         ...detailsVal,
      //       },
      //       value:
      //         value === 2 ? 'loader' : value === 1 ? 'hauler' : 'all fleet',
      //     },
      //     {
      //       ...data.loaders.devices
      //         .filter((item) => item.device_loads_per_day !== 0)
      //         .map((item) => {
      //           const asset = assets.find(
      //             (asset) => asset.device.deviceId === item.loader
      //           );
      //           const summary = data.loaders.deviceSummary.find((dev) => {
      //             return dev.loader === item.loader;
      //           });
      //           return {
      //             ...item,
      //             make: asset?.make,
      //             model: asset?.model,
      //             bumperId: asset?.bumperNumber,
      //             assetType: asset?.assetType,
      //             summary,
      //           };
      //         }),
      //     },
      //     fileNameWithDateTime,
      //     `${title}`,
      //     unitSystem,
      //     analyticsData as any[],
      //     {
      //       ...userSelections,
      //       quickrange:
      //         isSelected === 'Custom'
      //           ? `${calculateDaysDifference(
      //               startDate,
      //               endDate
      //             )} Days from ${convertStdDateString(
      //               startDate
      //             )} with ${aggregation} aggregation`
      //           : isSelected,
      //     },
      //     hoursBreakDown,
      //     suggestionData,
      //     {
      //       startDate,
      //       endDate,
      //     },
      //     loaderEvents
      //   );
      // }

      void handleExportClick('excel');
    }
  }, [exportTo]);

  const isNotLoadingData = useMemo(() => {
    return Object.entries(loadingStates)
      .filter(
        ([key]) => key !== 'isLoadingTarget' && key !== 'isLoadingSuggestions'
      )
      .every(([, value]) => !value);
  }, [loadingStates]);

  const fetchData = async (
    range: string,
    startDate: Date,
    endDate: Date,
    aggregation: string,
    signal?: AbortSignal
  ) => {
    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

    const shouldSendDate = isSelected === 'Custom' || isSelected === 'Today';
    if (isNotLoadingData) {
      await productionHandler.get(
        range,
        formattedStartDate,
        formattedEndDate,
        shouldSendDate,
        false,
        value === 2 ? 'loader' : value === 1 ? 'hauler' : 'all',
        aggregation.toLowerCase(),
        shiftWiseAnalysis,
        shiftNumber,
        signal
      );

      setInitRun(false);
    }
  };

  const fetchLoaderData = async (
    range: string,
    startDate: Date,
    endDate: Date,
    aggregation: string,
    signal?: AbortSignal
  ) => {
    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
    const shouldSendDate = isSelected === 'Custom' || isSelected === 'Today';

    await productionHandler.getLoaderDetails(
      range,
      formattedStartDate,
      formattedEndDate,
      shouldSendDate,
      false,
      value === 2 ? 'loader' : value === 1 ? 'hauler' : 'all',
      aggregation.toLowerCase(),
      shiftWiseAnalysis,
      shiftNumber,
      signal
    );
  };

  const handleFetchCustomDateRangeData = ({
    aggregation,
  }: {
    aggregation: string;
  }) => {
    // Updates are handled by useEffect reacting to state changes.
  };

  const handleFetchCustomOnAggregationChange = useCallback(
    ({
      aggregation,
      startDate,
      endDate,
    }: {
      aggregation: string;
      startDate: Date;
      endDate: Date;
    }) => {
      setAggregation(aggregation);
      setStartDate(startDate);
      setEndDate(endDate);
    },
    [setAggregation, setStartDate, setEndDate]
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    const shouldSendDate = isSelected === 'Custom' || isSelected === 'Today';

    if (debouncedStartDate && debouncedEndDate && accessToken) {
      if (shouldSendDate && !isCustomDateChanged) {
        setIsCustomDateChanged(true);
      }

      if (!shouldSendDate && isCustomDateChanged) {
        setIsCustomDateChanged(false);
      }

      const fetchAllData = async () => {
        try {
          // Future: pass `signal` into these handlers to cancel the network request
          await Promise.allSettled([
            fetchLoaderData(
              isSelected,
              debouncedStartDate,
              debouncedEndDate,
              debouncedAggregation,
              signal
            ),
            fetchData(
              isSelected,
              debouncedStartDate,
              debouncedEndDate,
              debouncedAggregation,
              signal
            ),
          ]);
        } catch (error) {
          console.error('Fetch aborted or failed', error);
        }
      };

      void fetchAllData();
    }

    return () => {
      abortController.abort();
    };
  }, [
    isSelected,
    debouncedStartDate,
    debouncedEndDate,
    debouncedAggregation,
    accessToken,
    shiftWiseAnalysis,
    debouncedShiftNumber,
  ]);

  const [isCalculatingAverages, setIsCalculatingAverages] = useState(true);
  const rollingDailyAverage = useRollingDailyAverage(data, selectedChartSource);
  const currentDailyAverage = useCurrentDailyAvgProd(data);

  useEffect(() => {
    if (
      rollingDailyAverage !== undefined &&
      currentDailyAverage !== undefined
    ) {
      setIsCalculatingAverages(false);
    }
  }, [rollingDailyAverage, currentDailyAverage]);

  useEffect(() => {
    dispatch(
      setIsLoading({ isLoadingFleetPeriodSummary: isCalculatingAverages })
    );
  }, [isCalculatingAverages, dispatch]);

  const handleLastHoursAnalysis = (anchor: HTMLElement) => {
    const today = dayjs().format('YYYY-MM-DD');
    const pageDate = dayjs(startDate).format('YYYY-MM-DD');
    const defaultDate =
      pageDate === today ? dayjs().subtract(1, 'day') : dayjs(startDate);
    setLastHoursPickedDate(defaultDate);
    setLastHoursAnchor(anchor);
  };

  const handleLastHoursConfirm = () => {
    setLastHoursModalDate(lastHoursPickedDate.format('YYYY-MM-DD'));
    setLastHoursAnchor(null);
    setIsLastHoursModalOpen(true);
  };

  return (
    <>
      <AiAnalysisModal
        open={isAiModalOpen}
        onClose={() => {
          setIsAiModalOpen(false);
        }}
        enableDarkTheme={enableDarkTheme}
      />
      {lastHoursModalDate && (
        <ProductivityLastHoursModal
          open={isLastHoursModalOpen}
          onClose={() => {
            setIsLastHoursModalOpen(false);
          }}
          enableDarkTheme={enableDarkTheme}
          date={lastHoursModalDate}
          lastHours={lastHoursPickedCount}
        />
      )}
      <Popover
        open={Boolean(lastHoursAnchor)}
        anchorEl={lastHoursAnchor}
        onClose={() => {
          setLastHoursAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            background: palette.boxBackground,
            border: `1px solid ${palette.cardBorderColor}`,
            borderRadius: '12px',
            p: '16px',
            width: '290px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            mt: '6px',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: palette.textPrimary,
            mb: '12px',
          }}
        >
          {t('choose_date_amp_hours')}
        </Typography>

        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: palette.textSecondary,
            mb: '6px',
          }}
        >
          {t('date')}
        </Typography>
        <CustomDatePicker
          value={lastHoursPickedDate}
          onChange={(val) => {
            if (val) setLastHoursPickedDate(val);
          }}
          disableFuture
          maxDate={dayjs().subtract(1, 'day')}
          sx={{ width: '100%', mb: '14px' }}
        />

        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: palette.textSecondary,
            mt: '14px',
            mb: '8px',
          }}
        >
          {t('hours_to_analyze')}
        </Typography>
        <Box sx={{ display: 'flex', gap: '6px' }}>
          {[2, 3, 4, 5, 6].map((h) => (
            <Box
              key={h}
              onClick={() => {
                setLastHoursPickedCount(h);
              }}
              sx={{
                flex: 1,
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                border:
                  lastHoursPickedCount === h
                    ? `1.5px solid ${palette.primary}`
                    : `1px solid ${palette.borderGrey}`,
                background:
                  lastHoursPickedCount === h
                    ? `${palette.primary}26`
                    : 'transparent',
                color:
                  lastHoursPickedCount === h
                    ? palette.primary
                    : enableDarkTheme
                    ? palette.textSecondary
                    : palette.textSecondary,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: palette.primary,
                  color: palette.primary,
                },
              }}
            >
              {h}h
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: '16px',
            gap: '8px',
          }}
        >
          <Button
            size="small"
            onClick={() => {
              setLastHoursAnchor(null);
            }}
            sx={{
              color: palette.textSecondary,
              fontSize: '12px',
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleLastHoursConfirm}
            sx={{
              fontSize: '12px',
              background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
              boxShadow: `0 2px 8px ${palette.primary}66`,
              '&:hover': {
                background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${palette.primary} 100%)`,
              },
            }}
          >
            {t('analyze')}
          </Button>
        </Box>
      </Popover>
      <HeaderMenu
        value={value}
        handleChange={handleChange}
        handleExport={setExportClicked}
        setExportTo={setExportTo}
        exportTo={exportTo}
        loadingData={isNotLoadingData}
        isPrinting={isPrinting}
        disabled={!['buz', 'myn'].includes(customerCode)}
        onAiAnalysis={() => {
          setIsAiModalOpen(true);
        }}
        onLastHoursAnalysis={handleLastHoursAnalysis}
      />
      <div id="production-overview">
        <Loader
          value={value}
          enableDarkTheme={enableDarkTheme}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          unitSystem={unitSystem}
          customerCode={customerCode}
          loadingStates={loadingStates}
          assetOverview={assetOverview}
          data={data.loaders}
          aggregation={aggregation}
          setAggregation={setAggregation}
          isPrinting={isPrinting}
          setAnalyticsData={setAnalyticsData}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
        />
        <Hauler
          value={value}
          enableDarkTheme={enableDarkTheme}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          unitSystem={unitSystem}
          customerCode={customerCode}
          loadingStates={loadingStates}
          assetOverview={assetOverview}
          data={data.allFleet}
          aggregation={aggregation}
          setAggregation={setAggregation}
          isPrinting={isPrinting}
          setDetailsVal={setDetailsVal}
          setAnalyticsData={setAnalyticsData}
          setHoursBreakDown={setHoursBreakDown}
          setUserSelections={setUserSelections}
          setSuggestionData={setSuggestionData}
          selectedChartSource={selectedChartSource}
          setSelectedChartSource={setSelectedChartSource}
          rollingDailyAverage={rollingDailyAverage}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
          // currentDailyAverage={currentDailyAverage}
        />
        <AllFleet
          value={value}
          enableDarkTheme={enableDarkTheme}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          data={data.allFleet}
          loadingStates={loadingStates}
          unitSystem={unitSystem}
          customerCode={customerCode}
          assetOverview={assetOverview}
          aggregation={aggregation}
          setAggregation={setAggregation}
          isPrinting={isPrinting}
          setDetailsVal={setDetailsVal}
          setAnalyticsData={setAnalyticsData}
          setHoursBreakDown={setHoursBreakDown}
          setUserSelections={setUserSelections}
          setSuggestionData={setSuggestionData}
          selectedChartSource={selectedChartSource}
          setSelectedChartSource={setSelectedChartSource}
          rollingDailyAverage={rollingDailyAverage}
          currentDailyAverage={currentDailyAverage}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
        />
      </div>
    </>
  );
};

export default Production;
